import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

export type CurrencyCode = 'USD' | 'EUR' | 'RUB';

export interface CurrencyOption {
  code: CurrencyCode;
  label: string;
  symbol: string;
}

@Injectable({
  providedIn: 'root'
})
export class CurrencyService {
  readonly currencies: CurrencyOption[] = [
    { code: 'USD', label: 'USD', symbol: '$' },
    { code: 'EUR', label: 'EUR', symbol: '€' },
    { code: 'RUB', label: 'RUB', symbol: '₽' }
  ];

  currentCurrency = signal<CurrencyCode>(
    (localStorage.getItem('currency') as CurrencyCode) || 'USD'
  );

  private rates: Partial<Record<CurrencyCode, number>> = {
    USD: 1,
    EUR: 1,
    RUB: 1
  };

  constructor(private http: HttpClient) {
    this.loadRates();
  }

  loadRates(): void {
    this.http.get<any>('https://open.er-api.com/v6/latest/USD').subscribe({
      next: (res) => {
        this.rates = {
          USD: 1,
          EUR: res.rates?.EUR ?? 1,
          RUB: res.rates?.RUB ?? 1
        };
      },
      error: () => {
        this.rates = {
          USD: 1,
          EUR: 0.92,
          RUB: 90
        };
      }
    });
  }

  setCurrency(code: CurrencyCode): void {
    this.currentCurrency.set(code);
    localStorage.setItem('currency', code);
  }

  getSymbol(): string {
    return this.currencies.find(c => c.code === this.currentCurrency())?.symbol || '$';
  }

  convertFromUsd(priceUsd: number): number {
    const rate = this.rates[this.currentCurrency()] || 1;
    return priceUsd * rate;
  }

  formatPrice(priceUsd: number): string {
    const value = this.convertFromUsd(priceUsd);

    const formatted = new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: this.currentCurrency() === 'RUB' ? 0 : 2,
      maximumFractionDigits: this.currentCurrency() === 'RUB' ? 0 : 2
    }).format(value);

    switch (this.currentCurrency()) {
      case 'USD':
        return `$${formatted}`;

      case 'EUR':
        return `€${formatted}`;

      case 'RUB':
        return `${formatted} ₽`;

      default:
        return `$${formatted}`;
    }
  }
}
